import React, { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Card, Col, Divider, Form, Row, Space, Switch, Input, Upload, Button, Select } from 'antd';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { validityOptions } from 'common/constans/guest-validity-options.constant';
import { guestTypeOptions } from 'common/constans/guest-type-options.constant';
import styles from './default-configuration.module.scss';
import { useMedia } from 'common/hooks/use-media/use-media.hook';
import SelectState from 'common/components/form-elements/select-state/select-state';
import RadioGroup from 'common/components/form-elements/radio-group/radio-group';
import { ISystemConfigurationUpdate } from 'common/services/configuration-service/configuration.service.types';
import configurationService from 'common/services/configuration-service/configuration.service';
import configurationStore from 'common/stores/configuration/configuration.store';
import { notification } from 'common/utils/notification';
import { MAX_FILE_SIZE_BYTES } from 'common/constans/common.constants';
import FileService from 'common/services/file-service/file.service';
import { entryTypeSelectOptions } from 'features/eiq-secure/constans/common.eiq-secure';

export const DefaultConfiguration: FC = observer(() => {
  const [form] = Form.useForm();
  const { isMobile } = useMedia();
  const [speedLimitImage, setSpeedLimitImage] = useState<Blob | null>();
  const [speedLimitPreviewImage, setSpeedLimitPreviewImage] = useState<string>('');

  const initData = configurationStore.configuration?.default;

  let timeoutId: any;

  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      form.submit();
    }, 1000);
  };

  const onFinish = async (values: any) => {
    if (speedLimitImage) {
      const photoUrl = await FileService.uploadPhoto(speedLimitImage);
      values.speedLimitImageUrl = photoUrl.absoluteUri;
    }
    if (!speedLimitImage && !speedLimitPreviewImage) {
      values.speedLimitImageUrl = '';
    }

    const model: ISystemConfigurationUpdate = {
      default: {
        guestValidity: values.guestValidity,
        guestType: values.guestType,
        isShowPin: values.isShowPin,
        defaultLicenseState: values.defaultLicenseState,
        expiredGuestRemoveDelayInDays: values.expiredGuestRemoveDelayInDays ? parseInt(values.expiredGuestRemoveDelayInDays) : null,
        vehicleDeletionGracePeriodDays: values.vehicleDeletionGracePeriodDays ? parseInt(values.vehicleDeletionGracePeriodDays) : null,
        isShowPinConnect: values.isShowPinConnect,
        isShareGuestConnect: values.isShareGuestConnect,
        isShareVehicleConnect: values.isShareVehicleConnect,
        isAlertMessageEditableForSecure: values.isAlertMessageEditableForSecure,
        isGuestListEditableInSecure: values.isGuestListEditableInSecure,
        isGuestRestrictionsEditableInSecure: values.isGuestRestrictionsEditableInSecure,
        isDisplayResidentOwnTrafficInConnect: values.isDisplayResidentOwnTrafficInConnect,
        isLimitTrafficHistoryInConnect: values.isLimitTrafficHistoryInConnect,
        isDisplaySharedGuestTrafficInConnect: values.isDisplaySharedGuestTrafficInConnect,
        entryType: values.entryType,
      },
      serviceHours: null,
      citations: null,
      guestLimitations: null,
      cardAccessConfiguration: null,
    };

    const result = await configurationService.updateSystemConfiguration(model);
    configurationStore.setConfiguration(result);
    notification.success({
      message: 'Configurations successfully updated',
      duration: 5,
    });
  };

  useEffect(() => {
    if (form) {
      form.setFieldsValue(initData);
    }
    if (initData?.speedLimitImageUrl) {
      setSpeedLimitPreviewImage(initData?.speedLimitImageUrl);
    } else {
      setSpeedLimitPreviewImage('');
      setSpeedLimitImage(null);
    }
  }, [form, initData]);

  const uploadProps = {
    beforeUpload: async (file: Blob) => {
      if (file.size >= MAX_FILE_SIZE_BYTES) {
        notification.error({ message: 'The image size must be less than 5 MB!' });
      } else {
        setSpeedLimitImage(file);
        try {
          const dataURL = await readImageFile(file);
          setSpeedLimitPreviewImage(dataURL);
          handleFormValuesChange(null, null);
        } catch (error) {
          console.error('Error reading file');
        }
      }
      return false;
    },
    maxCount: 1,
    accept: 'image/png, image/jpeg, image/jpg',
    showUploadList: false,
  };

  const readImageFile = (file: Blob) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  };

  const onRemoveSpeedLimitImage = () => {
    setSpeedLimitImage(null);
    setSpeedLimitPreviewImage('');
    handleFormValuesChange(null, null);
  };

  return (
    <FormWrapper form={form} onFinish={onFinish} onValuesChange={handleFormValuesChange} initialValues={initData} layout="vertical">
      <Card className="eiq-card eiq-card-border eiq-configuration">
        <Row className="eiq-card-header">
          <Col span={24}>
            <h1 className="h1">Local Defaults Configuration</h1>
          </Col>
        </Row>
        <Row>
          <Col xs={24} sm={12}>
            <div className={styles.configurationDefaultWrapper}>
              <h3 className="h3">Default Guest Options</h3>
              <h4 className={`h4 ${styles.italic}`}>Default options when adding a guest to a resident's guest list</h4>
              <Space className="full-width">
                <Form.Item name="guestValidity" label={<label className={styles.label}>Validity</label>}>
                  <RadioGroup options={validityOptions} />
                </Form.Item>
              </Space>
              <Space className="full-width">
                <Form.Item name="guestType" label={<label className={styles.label}>Guest type</label>}>
                  <RadioGroup options={guestTypeOptions} />
                </Form.Item>
              </Space>
              <Space className="full-width" style={{ flexDirection: 'column' }}>
                <span className={styles.label}>Expired guest removal (days)</span>
                <span className={`${styles.italic} ${styles.description}`}>
                  Days an expired guest remains in a resident's guest list before being removed
                </span>
                <Form.Item name="expiredGuestRemoveDelayInDays">
                  <Input type="number" min={0} max={365} />
                </Form.Item>
              </Space>
              <Space className="full-width" style={{ flexDirection: 'column' }}>
                <span className={styles.label}>Deleted vehicle removal (days)</span>
                <span className={`${styles.italic} ${styles.description}`}>
                  Days a deleted vehicle remains in EIQ-Manage before being permanently removed
                </span>
                <Form.Item name="vehicleDeletionGracePeriodDays">
                  <Input type="number" min={0} max={365} />
                </Form.Item>
              </Space>
            </div>
            {isMobile && <Divider className="eiq-divider" />}
          </Col>
          <Col xs={24} sm={12}>
            <h3 className="h3">Default Local Options</h3>
            <Space className="full-width">
              <div className={styles.switch}>
                <div>
                  <span>Guest lists are always shared</span>
                  <span className={styles.switchDescription}>Residents cannot disable sharing their guests with other occupants.</span>
                </div>
                <Form.Item name="isShareGuestConnect" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width">
              <div className={styles.switch}>
                <div>
                  <span>Vehicles are always shared</span>
                  <span className={styles.switchDescription}>Residents cannot disable sharing their vehicles with other occupants.</span>
                </div>
                <Form.Item name="isShareVehicleConnect" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width">
              <div className={styles.switch}>
                <span>Display PIN in EIQ-Secure</span>
                <Form.Item name="isShowPin" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width">
              <div className={styles.switch}>
                <span>Display PIN in EIQ-Connect</span>
                <Form.Item name="isShowPinConnect" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width">
              <Form.Item name="defaultLicenseState" label="License Plate State">
                <SelectState placeholder="Select a license plate state" />
              </Form.Item>
            </Space>
            <Space className="full-width">
              <div className={styles.switch}>
                <span>Enable alert message editing in EIQ-Secure</span>
                <Form.Item name="isAlertMessageEditableForSecure" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width">
              <div className={styles.switch}>
                <span>Enable guest list editing in EIQ-Secure</span>
                <Form.Item name="isGuestListEditableInSecure" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width">
              <div className={styles.switch}>
                <span>Enable guest restrictions editing in EIQ-Secure</span>
                <Form.Item name="isGuestRestrictionsEditableInSecure" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width">
              <Form.Item name="entryType" label={<label className={styles.label}>Default Admit Resident Entry Type</label>}>
                <Select placeholder="Select entry type" options={entryTypeSelectOptions} />
              </Form.Item>
            </Space>
            <Space className="full-width">
              <div className={styles.switch}>
                <span>Display Resident's Own Vehicle Traffic</span>
                <Form.Item name="isDisplayResidentOwnTrafficInConnect" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width">
              <div className={styles.switch}>
                <span>Limit Traffic History to 30 Days</span>
                <Form.Item name="isLimitTrafficHistoryInConnect" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
            <Space className="full-width">
              <div className={styles.switch}>
                <span>Display shared guest traffic to resident</span>
                <Form.Item name="isDisplaySharedGuestTrafficInConnect" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>
    </FormWrapper>
  );
});
